import { Link } from 'react-router-dom';
import DeckMenu from './DeckMenu.tsx';
import { DECK_DRAG_MIME } from '../../components/CategorySidebar.tsx';
import { relativeTime, colourHex, formatBuiltBy, formatBuiltByShort } from '../../lib/format.ts';
import { LayoutGrid, Refresh, Lock } from '../../lib/icons.tsx';
import type { Dispatch, SetStateAction } from 'react';
import type { CoverWarm } from '../../lib/coverWarm.ts';
import type { TileSize } from '../../components/TileSizeControl.tsx';
import type { DeckSummary } from '../../../shared/deck.ts';
import type { CategoryColour } from '../../../shared/api.ts';
import { swatchVar } from '../../../shared/palette.ts';
import type { DeckCardActions } from './DeckMenu.tsx';

export type { DeckCardActions } from './DeckMenu.tsx';

// Card-size steps — the grid's minmax() minimum (see .deck-grid). `md` went
// 250 → 300 when the card grew an inset cover: at 250 the thumb and its margins
// left the title barely 110px and every deck name wrapped to four lines. `lg` is
// unchanged — its cover spans the whole column, so it is already larger at any
// width, and raising the minimum would only cost columns.
//
// `sm` went 200 → 170 on 2026-08-07 because at 200 it was not a SMALL size where
// it mattered. Between 768 and 1024 the category sidebar takes a fixed 220px plus
// its gap, leaving the grid ~629px at 900: three 200px columns need 648px and
// miss by ~19px, so small and medium both collapsed to two columns and picking
// "Small cards" did nothing. Worse, each of those two tracks then rendered ~306px
// — wider than a medium card elsewhere on the page.
export const TILE_MIN: Record<TileSize, string> = { sm: '170px', md: '300px', lg: '320px' };

/** Everything the kebab menu (and the cover's zoom button) can do to one deck.
 *  All of it lives on the dashboard, which owns the modals each one opens. */
type DeckCardProps = {
  deck: DeckSummary;
  /** Identity of this card's menu within the page's single open-menu slot. */
  cardKey: string;
  tileSize: TileSize;
  /** The cached cover URL, or null while there isn't one. */
  cover: string | null;
  /** The dashboard's warm queue — placeholder state, retry, and the observer
   *  ref that turns "this card is on screen" into "render its cover". */
  warm: CoverWarm;
  /** This deck's overview is being fetched — the zoom button says so. */
  overviewLoading: boolean;
  busy: boolean;
  isSelected: boolean;
  /** Something, anywhere in the grid, is ticked: the checkboxes stay visible. */
  isSelecting: boolean;
  isDragging: boolean;
  menuOpen: boolean;
  /** A category's registry tint, for its chip dot — the same function the rows
   *  view takes. Optional: a caller with no registry to hand renders the chips
   *  untinted rather than dropping them. */
  categoryColour?: (path: string) => CategoryColour | null;
  onToggleSelected: (slug: string, shiftKey: boolean) => void;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  setDraggingSlug: Dispatch<SetStateAction<string | null>>;
  actions: DeckCardActions;
};

/** One deck on the grid: cover, title, chips, its kebab menu, the select
 *  checkbox, and the drag source that files it into a category.
 *
 *  The design's **owner avatar and star** are not built, on the rows view's
 *  reasoning: Studio has no identity and `DeckSummary` has no starred flag, so
 *  both would be dead controls. The design's ⋯ slot is this card's existing
 *  kebab. See `plans/studio-homepage-redesign.md`. */
export default function DeckCard({
  deck: d,
  cardKey,
  tileSize,
  cover,
  warm,
  overviewLoading,
  busy,
  isSelected,
  isSelecting,
  isDragging,
  menuOpen,
  categoryColour,
  onToggleSelected,
  setMenuKey,
  setDraggingSlug,
  actions,
}: DeckCardProps) {
  const { warmingNow, coverStalled, retryCover, coverRef } = warm;
  // The cover: slide 1, from the same cached 640×360 WebP the deck overview
  // uses. Small cards show none at all; a broken or empty deck has nothing to
  // render, so it is never given a slot or queued for one.
  const showCover = tileSize !== 'sm' && !d.broken && d.slideCount > 0;
  const modelLabel = d.builtBy ? formatBuiltByShort(d.builtBy) : '';
  // "See all slides" — the only way to see the REST of a deck without opening
  // the editor, so it exists at every size. WHERE it goes is the size's
  // business: see the two render sites below.
  const zoomEl =
    !d.broken && d.slideCount > 0 ? (
      <button
        type="button"
        className={'deck-card__zoom' + (overviewLoading ? ' is-loading' : '')}
        title="See all slides"
        aria-label={`See all slides in ${d.title}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          actions.onOpenOverview(d);
        }}
      >
        <LayoutGrid />
      </button>
    ) : null;
  const coverEl = showCover ? (
    // The 16:9 box is reserved before any image lands, so a cover arriving
    // mid-session never reflows the grid under the user.
    // aria-hidden: the title sits right beside it and names the deck — but NOT
    // when the box holds the retry control, because a focusable element inside
    // an aria-hidden subtree is reachable by keyboard and invisible to a screen
    // reader at the same time. The button names itself.
    // ref: registers the card with the warm queue's observer — scrolling to a
    // card is what asks for its cover.
    <div
      className="deck-card__cover"
      aria-hidden={coverStalled(d.slug) && !cover ? undefined : 'true'}
      ref={cover ? undefined : coverRef(d.slug)}
    >
      {cover ? (
        // draggable={false} is load-bearing: the CARD is the drag source for
        // filing into a category, and an <img> is natively draggable, so
        // without this it hijacks the drag with an image payload the category
        // rail can't read — the same reason .deck-card__main carries it.
        <img className="deck-card__cover-img" src={cover} alt="" loading="lazy" decoding="async" draggable={false} />
      ) : coverStalled(d.slug) ? (
        // Given up on for this session. It used to stop here — a permanently
        // grey box with nothing to press, which breaks the studio's own rule
        // that a warning always offers a way out. The overview and the Library
        // both put the way back on the tile itself; this is the same control.
        <button
          type="button"
          className="deck-card__cover-retry"
          title="This cover didn't render. Try again."
          aria-label={`Retry the cover for ${d.title || d.slug}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            retryCover(d.slug);
          }}
        >
          <Refresh size={16} />
        </button>
      ) : (
        // Still only while something is actually coming: animating a ghost for
        // a deck nothing is working on promises an image that never arrives.
        <span className={'deck-card__cover-ph' + (warmingNow(d.slug) ? ' is-working' : '')} />
      )}
      {/* LARGE only: the cover is a full-width image with room to spare, so
          the button sits in its bottom-right the way a photo's controls do.
          Medium's cover is 80x45 — a 28px button would take ~40% of it — so
          medium keeps the in-flow placement alongside small. */}
      {tileSize === 'lg' && zoomEl}
    </div>
  ) : null;
  return (
    <li
      className={
        'deck-card deck-card--' +
        tileSize +
        (menuOpen ? ' deck-card--menu-open' : '') +
        (isSelected ? ' is-selected' : '') +
        (isSelecting ? ' is-selecting' : '') +
        (isDragging ? ' is-dragging' : '')
      }
      // Dragging a card onto a sidebar category files it there. The drag
      // source is the CARD, not the link inside it (see draggable={false} on
      // .deck-card__main): otherwise the browser drags the URL, with a link
      // ghost and a text/uri-list payload the rail can't recognise.
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DECK_DRAG_MIME, d.slug);
        e.dataTransfer.setData('text/plain', d.title); // dropping elsewhere pastes something sane
        e.dataTransfer.effectAllowed = 'copy';
        setDraggingSlug(d.slug);
        setMenuKey(null);
      }}
      onDragEnd={() => setDraggingSlug(null)}
    >
      {/* Select for a bulk verb. Hidden until hover (or until something IS
          selected) so the resting grid stays a wall of decks, not a form —
          but it is a real checkbox, so keyboard and screen readers get it at
          all times. Shift-click extends from the last one ticked. */}
      <label className="deck-card__select" onMouseDown={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            // React's onChange gives no shiftKey, so the range lives here and
            // onChange is left off entirely (the box is driven by `checked`).
            onToggleSelected(d.slug, e.shiftKey);
          }}
          onChange={() => {}}
          aria-label={`Select ${d.title}`}
        />
      </label>
      <DeckMenu
        deck={d}
        cardKey={cardKey}
        menuOpen={menuOpen}
        busy={busy}
        setMenuKey={setMenuKey}
        actions={actions}
        className="deck-card__menu iconbtn"
      />
      <Link to={`/editor/${d.slug}`} className="deck-card__main" draggable={false}>
        {/* The stripe stays FIRST, at the very top with the card's rounded
            corners — it is the deck's section signature, and a large cover
            sitting edge to edge underneath it never has to round its own. */}
        <div className="deck-card__stripe" aria-hidden="true">
          {(d.sections || [])
            .filter((s) => s.n !== null)
            .map((s) => (
              <span key={s.key} className="deck-card__band" style={{ background: colourHex(s.color) }} />
            ))}
        </div>
        {tileSize === 'lg' && coverEl}
        <div className="deck-card__body">
          {tileSize === 'md' && coverEl}
          {/* Wrapped at every size so there is one JSX shape and only the
              cover's POSITION varies: a plain block at sm/lg, the second
              column of the flex row at md. */}
          <div className="deck-card__text">
            {/* title attribute on both lines: each is truncated (the name at
                two lines, the slug at one), so the full text has to be
                recoverable without opening the deck. */}
            <h2 className="deck-card__title" title={d.title}>
              {d.title}
              {d.broken && <span className="badge badge--warn">Broken</span>}
            </h2>
            <p className="deck-card__slug" title={d.slug}>
              {d.slug}
            </p>
            <p className="deck-card__meta">
              {d.slideCount || '–'} slides · {relativeTime(d.lastEditedAt)}
            </p>
            {/* One chip family on its own row: status first, then provenance,
                then filing. Chips truncate rather than wrap — the full engine
                stamp (provider, effort) lives in the tooltip and the deck
                info modal, not on the card.

                Rendered even when a deck has no chips at all, unlike before:
                the row's height is what keeps every card in a grid track the
                same height, and a deck with no category and no engine stamp
                would otherwise sit a row shorter than its neighbours. */}
            <p className="deck-card__tags">
              {/* Private or shared reads BEFORE published: the two answer
                  different questions (who in the studio, versus who outside it)
                  and the restricting one is the surprising half. */}
              {d.restrictedTo !== null && (
                <span
                  className="tag-chip tag-chip--engine"
                  title={
                    d.restrictedTo.length === 0
                      ? 'Only its owner and studio admins can open this deck'
                      : `Shared with ${d.restrictedTo.length} ${d.restrictedTo.length === 1 ? 'person' : 'people'}`
                  }
                >
                  <Lock size={11} />
                  {d.restrictedTo.length === 0 ? 'Private' : 'Shared'}
                </span>
              )}
              {d.publishedUrl && (
                <span className="tag-chip tag-chip--ok">
                  <span className="tag-chip__dot" aria-hidden="true" />
                  Published
                </span>
              )}
              {modelLabel && (
                <span className="tag-chip tag-chip--engine" title={`Built by ${formatBuiltBy(d.builtBy)}`}>
                  {modelLabel}
                </span>
              )}
              {d.category && (
                <span className="tag-chip" title={`Category: ${d.category}`}>
                  {/* The dot's colour is a SWATCH NAME from the registry, never
                      a literal — the same set the category picker offers. */}
                  <span
                    className="tag-chip__dot"
                    style={{
                      background: categoryColour?.(d.category) ? swatchVar(categoryColour(d.category)) : 'var(--faint)',
                    }}
                    aria-hidden="true"
                  />
                  {d.category.replace('/', ' › ')}
                </span>
              )}
            </p>
          </div>
        </div>
      </Link>
      {/* SMALL and MEDIUM keep the original placement: pinned to the card's
          bottom-right, a sibling of __main so the card's own `overflow:
          visible` applies. The overlap that made this a bug is held off by
          reserved space on the meta row rather than by moving the button —
          see `.deck-card__meta`'s padding. LARGE renders it inside the cover
          instead (in `coverEl`), against the image. */}
      {tileSize !== 'lg' && zoomEl}
    </li>
  );
}

/** Ghost tile at the end of the grid — same action as the top-bar button. */
export function NewDeckTile({ onClick }: { onClick: () => void }) {
  return (
    <li className="deck-card deck-card--ghost">
      <button type="button" className="deck-card__ghost" onClick={onClick}>
        + New deck
      </button>
    </li>
  );
}
